import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Printer, Download, Loader2 } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/lib/supabaseClient';
import { fetchInvoiceForOrder, fetchInvoiceAssets } from '@/lib/invoiceApi';
import { getPaymentStatus, buildInvoicePaymentInfo } from '@/lib/paymentStatus';
import { downloadInvoicePdf, printInvoicePdf, willFitOnOnePage } from '@/lib/downloadInvoicePdf';
import { InvoiceDocument, type InvoiceTemplateData, type InvoiceRequestData } from '@/components/invoices/InvoiceDocument';

const PAYMENT_BADGE_CLASS: Record<'success' | 'warning' | 'neutral', string> = {
  success: 'bg-green-100 text-green-700',
  warning: 'bg-[#F0EFED] text-[#8A8A8A]',
  neutral: 'bg-gray-100 text-gray-500',
};

interface InvoiceViewProps {
  orderId: string;
  orderStatus: string;
  paymentMethod: string | null;
  codAmountBdt: number | null;
  deliveredAt: string | null;
}

export default function InvoiceView({ orderId, orderStatus, paymentMethod, codAmountBdt, deliveredAt }: InvoiceViewProps) {
  const { t } = useTranslation();
  const { session } = useAuth();
  const [request, setRequest] = useState<InvoiceRequestData | null>(null);
  const [template, setTemplate] = useState<InvoiceTemplateData | null>(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [gatewayNames, setGatewayNames] = useState<Record<string, string>>({});
  const invoiceRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Public, non-admin-gated view (this page runs as a logged-in
    // customer, not staff) — see core_payment_gateways_public_v.
    supabase
      .from('core_payment_gateways_public_v')
      .select('gateway_key, display_name')
      .then(({ data }) => setGatewayNames(Object.fromEntries(((data as { gateway_key: string; display_name: string }[]) ?? []).map((g) => [g.gateway_key, g.display_name]))));
  }, []);

  useEffect(() => {
    fetchInvoiceForOrder(orderId).then((result) => {
      if (!result) {
        setLoading(false);
        return;
      }
      setRequest(result.request);
      const tpl = result.template;
      if (session?.access_token) {
        fetchInvoiceAssets(orderId, session.access_token).then((assets) => {
          setTemplate({
            ...tpl,
            logo_url: assets?.logoUrl ?? null,
            signature_url: assets?.signatureImageUrl ?? null,
            stamp_url: assets?.stampImageUrl ?? null,
            watermark_url: assets?.watermarkUrl ?? null,
          });
        });
      } else {
        setTemplate(tpl);
      }
      setLoading(false);
    });
  }, [orderId, session?.access_token]);

  if (loading || !request || !template) return null;

  const paymentStatus = getPaymentStatus(orderStatus, paymentMethod);
  const payment = buildInvoicePaymentInfo(orderStatus, paymentMethod, codAmountBdt, deliveredAt, gatewayNames);

  const handleDownload = async () => {
    if (!invoiceRef.current) return;
    setDownloading(true);
    try {
      await downloadInvoicePdf(invoiceRef.current, `${request.doc_number || 'invoice'}.pdf`);
    } finally {
      setDownloading(false);
    }
  };

  // Prints natively for the common single-page case (crisper than any
  // rasterized PDF) and only falls back to the same pipeline Download
  // uses when the invoice genuinely spans multiple pages — native
  // print's fixed footer repeats on every page regardless of where that
  // page's real content ends, so it can't paginate a long invoice (many
  // scanned serials) correctly. See willFitOnOnePage's own comment.
  const handlePrint = async () => {
    if (!invoiceRef.current) return;
    if (willFitOnOnePage(invoiceRef.current)) {
      window.print();
      return;
    }
    setPrinting(true);
    try {
      await printInvoicePdf(invoiceRef.current);
    } finally {
      setPrinting(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-3 print:hidden">
        <div className="flex items-center gap-2">
          <h4 className="text-sm tracking-wider uppercase font-medium text-[#2A2A2A]">{t('order.invoice', 'Invoice')}</h4>
          <span className={`text-[10px] uppercase tracking-wider px-2.5 py-0.5 rounded-full ${PAYMENT_BADGE_CLASS[paymentStatus.tone]}`}>
            {paymentStatus.label}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleDownload}
            disabled={downloading}
            className="flex items-center gap-1.5 px-4 py-2 rounded-full border border-[#E8E8E6] text-xs font-medium text-[#2A2A2A] hover:border-[#7B1113] hover:text-[#7B1113] transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {downloading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
            {t('order.downloadInvoice', 'Download PDF')}
          </button>
          <button
            onClick={handlePrint}
            disabled={printing}
            className="flex items-center gap-1.5 px-4 py-2 rounded-full border border-[#E8E8E6] text-xs font-medium text-[#2A2A2A] hover:border-[#7B1113] hover:text-[#7B1113] transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {printing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Printer className="w-3.5 h-3.5" />}
            {t('order.printInvoice', 'Print')}
          </button>
        </div>
      </div>

      <div className="rounded-2xl bg-[#EEF1F6] p-3 md:p-8 overflow-auto print:p-0 print:bg-white print:rounded-none">
        <div ref={invoiceRef} className="shadow-[0_8px_40px_-8px_rgba(27,42,74,0.35)] print:shadow-none mx-auto" style={{ width: 'fit-content' }}>
          <InvoiceDocument template={template} request={request} payment={payment} />
        </div>
      </div>
    </div>
  );
}
